/**
 * Route preload registry
 * ──────────────────────
 * Single source of truth for the dynamic `import()` factories of every route
 * that appears in a role's nav config. Consumed both by `React.lazy()` in the
 * router (`src/App.tsx`) AND by the role-aware idle preloader / intent
 * preloader below, so the browser treats preload + render as the same module
 * request (identical specifier → identical chunk).
 *
 * DO NOT re-inline `() => import("./pages/...")` for any route also listed in
 * `routeImports` — that would break the dedupe guarantee.
 */

import type {
  AdminNavConfig,
  NavRouteItem,
  SectionRouteConfig,
} from "@/components/NavShell/navRouteConfig";
import {
  ADMIN_NAV,
  REGULAR_ADMIN_NAV,
  CENTRE_MANAGER_NAV,
  TUTOR_NAV,
  STATE_PARTNER_NAV,
  GROWTH_PARTNER_NAV,
  FRANCHISEE_PROSPECT_NAV,
  PARENT_NAV,
  STUDENT_NAV,
} from "@/components/NavShell/navRouteConfig";
import { useCallback, useRef } from "react";

// ── Page import factories (one literal per module) ─────────────────────────
export const pageImports = {
  NationalDashboard: () => import("@/pages/NationalDashboard"),
  GrowthTeamPage: () => import("@/pages/GrowthTeamPage"),
  LearningDashboard: () => import("@/pages/learning/LearningDashboard"),
  CoursesPage: () => import("@/pages/learning/CoursesPage"),
  MicrolearningPage: () => import("@/pages/learning/MicrolearningPage"),
  CompetencyDashboardPage: () => import("@/pages/learning/CompetencyDashboardPage"),
  LearningPathManagement: () => import("@/pages/learning/LearningPathManagement"),
  MyProgressPage: () => import("@/pages/learning/MyProgressPage"),
  LearningAnalyticsPage: () => import("@/pages/learning/LearningAnalyticsPage"),
  AdminSettingsPage: () => import("@/pages/learning/AdminSettingsPage"),
  AccessAuditLogsPage: () => import("@/pages/learning/AccessAuditLogsPage"),
  TutorAuditPage: () => import("@/pages/learning/TutorAuditPage"),
  AssessmentDashboard: () => import("@/pages/assessment/AssessmentDashboard"),
  AssessmentQuizzesPage: () => import("@/pages/assessment/AssessmentQuizzesPage"),
  AssessmentResultsPage: () => import("@/pages/assessment/AssessmentResultsPage"),
  AssessmentExamsPage: () => import("@/pages/assessment/AssessmentExamsPage"),
  AssessmentScoringPage: () => import("@/pages/assessment/AssessmentScoringPage"),
  AssessmentStudentsPage: () => import("@/pages/assessment/AssessmentStudentsPage"),
  AtRiskStudentsPage: () => import("@/pages/assessment/AtRiskStudentsPage"),
  EndOfTermPage: () => import("@/pages/assessment/EndOfTermPage"),
  EndOfTermResultsPage: () => import("@/pages/assessment/EndOfTermResultsPage"),
  QuestionBankPage: () => import("@/pages/assessment/QuestionBankPage"),
  HomeworkVideos: () => import("@/pages/HomeworkVideos"),
  HomeworkHandoutsPage: () => import("@/pages/homework/HomeworkHandoutsPage"),
  ExamResultsReportPage: () => import("@/pages/exam/ExamResultsReportPage"),
  
  NotificationsHubPage: () => import("@/pages/NotificationsHubPage"),
  AllUsers: () => import("@/pages/users/AllUsers"),
  LocationsManagement: () => import("@/pages/LocationsManagement"),
  ProfilePage: () => import("@/pages/ProfilePage"),
  ParentProfilePage: () => import("@/pages/parent/ParentProfilePage"),
  ParentDashboard: () => import("@/pages/ParentDashboard"),
  ParentHomeworkPage: () => import("@/pages/parent/ParentHomeworkPage"),
  ParentExamResultsPage: () => import("@/pages/parent/ParentExamResultsPage"),
  DownloadableResources: () => import("@/pages/DownloadableResources"),
  UnderstandingLurneax: () => import("@/pages/UnderstandingLurneax"),
  RunningEOTGuide: () => import("@/pages/RunningEOTGuide"),
  RunningExamGuide: () => import("@/pages/RunningExamGuide"),
  HomeworkGuide: () => import("@/pages/HomeworkGuide"),
} as const;

// ── Nav-config path → import factory ───────────────────────────────────────
// Query strings are stripped for lookup; multiple nav paths may resolve to
// the same underlying page (e.g. scoring?scope=eot / scoring?scope=exam).
export const routeImports: Record<string, () => Promise<unknown>> = {
  "/national-dashboard": pageImports.NationalDashboard,
  "/learning/dashboard": pageImports.LearningDashboard,
  "/learning/courses": pageImports.CoursesPage,
  "/learning/microlearning": pageImports.MicrolearningPage,
  "/learning/competency": pageImports.CompetencyDashboardPage,
  "/learning/paths": pageImports.LearningPathManagement,
  "/learning/my-progress": pageImports.MyProgressPage,
  "/learning/learning-analytics": pageImports.LearningAnalyticsPage,
  "/learning/admin-settings": pageImports.AdminSettingsPage,
  "/learning/access-audit-logs": pageImports.AccessAuditLogsPage,
  "/learning/tutor-audit": pageImports.TutorAuditPage,
  "/assessment/dashboard": pageImports.AssessmentDashboard,
  "/assessment/diagnostic-tests": pageImports.AssessmentQuizzesPage,
  "/assessment/results": pageImports.AssessmentResultsPage,
  "/assessment/exams": pageImports.AssessmentExamsPage,
  "/assessment/scoring": pageImports.AssessmentScoringPage,
  "/assessment/students": pageImports.AssessmentStudentsPage,
  "/assessment/students/archived": pageImports.AssessmentStudentsPage,
  "/assessment/early-warning": pageImports.AtRiskStudentsPage,
  "/assessment/end-of-term": pageImports.EndOfTermPage,
  "/assessment/end-of-term-results": pageImports.EndOfTermResultsPage,
  "/assessment/question-bank": pageImports.QuestionBankPage,
  "/assessment/exam-results": pageImports.ExamResultsReportPage,
  "/assessment/my-exam-results": pageImports.ParentExamResultsPage,
  "/assessment/homework-videos": pageImports.HomeworkVideos,
  "/assessment/homework-handouts": pageImports.HomeworkHandoutsPage,
  
  "/comms-center": pageImports.NotificationsHubPage,
  "/users": pageImports.AllUsers,
  "/locations": pageImports.LocationsManagement,
  "/profile": pageImports.ProfilePage,
  "/parent-dashboard": pageImports.ParentDashboard,
  "/parent/homework": pageImports.ParentHomeworkPage,
  "/parent/profile": pageImports.ParentProfilePage,
  "/resources/downloads": pageImports.DownloadableResources,
  "/understanding-lurneax": pageImports.UnderstandingLurneax,
  "/running-eot-guide": pageImports.RunningEOTGuide,
  "/running-exam-guide": pageImports.RunningExamGuide,
  "/homework-guide": pageImports.HomeworkGuide,
};

// ── Fired-once memo so intent preload doesn't re-import per hover ─────────
const fired = new Set<string>();

function normalisePath(path: string): string {
  const q = path.indexOf("?");
  return q === -1 ? path : path.slice(0, q);
}

export function preloadPath(path: string | undefined): void {
  if (!path) return;
  const key = normalisePath(path);
  if (fired.has(key)) return;
  const importer = routeImports[key];
  if (!importer) return;
  fired.add(key);
  importer().catch(() => {
    // Non-fatal; leave in `fired` so we don't retry-storm.
  });
}

// ── Role → AdminNavConfig ─────────────────────────────────────────────────
export function navConfigForRole(
  role: string | null | undefined,
  allRoles: string[] = []
): AdminNavConfig {
  const isProspect =
    role === "franchisee_prospect" || allRoles.includes("franchisee_prospect");
  if (isProspect) return FRANCHISEE_PROSPECT_NAV;
  switch (role) {
    case "master_admin": return ADMIN_NAV;
    case "admin": return REGULAR_ADMIN_NAV;
    case "center_manager": return CENTRE_MANAGER_NAV;
    case "tutor": return TUTOR_NAV;
    case "state_partner": return STATE_PARTNER_NAV;
    case "growth_partner": return GROWTH_PARTNER_NAV;
    case "parent": return PARENT_NAV;
    case "student": return STUDENT_NAV;
    default: return REGULAR_ADMIN_NAV;
  }
}

// Recursively collect every nav path in a section (top-level + children + group children).
function collectPathsFromSection(section: SectionRouteConfig, out: Set<string>): void {
  function visit(item: NavRouteItem): void {
    if (item.path) out.add(item.path);
    if (item.children) for (const child of item.children) visit(child);
  }
  for (const item of section.items) visit(item);
  if (section.groups) for (const g of section.groups) for (const item of g.items) visit(item);
}

export function pathsForRole(
  role: string | null | undefined,
  allRoles: string[] = []
): string[] {
  const config = navConfigForRole(role, allRoles);
  const out = new Set<string>();
  for (const sectionKey of config.rail) {
    const section = config.sections[sectionKey];
    if (!section) continue;
    collectPathsFromSection(section, out);
  }
  return Array.from(out);
}

export function preloadRoleRoutes(
  role: string | null | undefined,
  allRoles: string[] = []
): void {
  for (const p of pathsForRole(role, allRoles)) preloadPath(p);
}

// ── Intent preload hook ───────────────────────────────────────────────────
export function usePreloadOnIntent(path: string | undefined) {
  const firedRef = useRef(false);
  const trigger = useCallback(() => {
    if (firedRef.current) return;
    firedRef.current = true;
    preloadPath(path);
  }, [path]);
  return {
    onPointerEnter: trigger,
    onFocus: trigger,
    onTouchStart: trigger,
  };
}
